// ── Tab definitions ───────────────────────────────────────
export const tabs = [
  "System Characteristics",
  "Information Types",
  "Components",
  "Inventory",
  "Control Implementations",
  "System Users",
  "Interconnections",
  "Locations",
  "Roles & Parties",
  "Leveraged Authorizations",
] as const;
export type TabId = (typeof tabs)[number];

export const TAB_DESCRIPTIONS: Record<TabId, string> = {
  "System Characteristics":
    "Defines the system’s identity, purpose, and security posture within its authorization boundary. It describes what the system does, where it operates, and the type of data and workloads it handles. This section establishes the baseline context auditors and engineers rely on to understand scope and criticality.",
  "Information Types":
    "Identifies the categories of data processed, stored, or transmitted by the system, along with their sensitivity and regulatory impact. It maps data types to confidentiality, integrity, and availability requirements. This ensures controls are aligned with actual data risk, not assumptions.",
  Components:
    "Breaks down the system into logical and physical components such as services, infrastructure, and platforms. Each component represents a control enforcement boundary where security responsibilities are applied. This view connects architecture directly to compliance execution.",
  Inventory:
    "Maintains a real-time list of discovered assets under each component, including systems, users, and resources. It tracks metadata like source, timestamps, and environment classification. This ensures compliance is evaluated on actual assets, not static assumptions.",
  "Control Implementations":
    "Manage how each security control is implemented — author what your system provides for consumers (Provider Export), discover and inherit controls from other systems (Consumer Inheritance), and write satisfied statements for shared responsibilities.",
  "System Users":
    "Captures all users and identities interacting with the system, including roles, privileges, and access boundaries. It highlights privileged accounts and enforces least privilege principles. This section is critical for validating access control and identity governance.",
  Interconnections:
    "Documents all external systems, services, and dependencies that interact with the system. It defines trust boundaries, data flows, and security considerations for each connection. This ensures risks from integrations and third-party dependencies are explicitly managed.",
  Locations:
    "Specifies where system components and data are hosted, including regions, data centers, or cloud zones. It highlights jurisdictional and compliance implications tied to geography. This is essential for regulatory alignment and data residency requirements.",
  "Roles & Parties":
    "Defines organizational roles, responsibilities, and stakeholders involved in system operation and security. It maps ownership for controls, components, and remediation actions. This ensures accountability is clearly assigned and auditable.",
  "Leveraged Authorizations":
    "Identifies external systems or services whose existing authorizations are reused or inherited. It explains the scope and limitations of relying on these authorizations. This prevents over-assumption of compliance and clarifies shared responsibility boundaries.",
};

export function formatTabName(tabName: string): string {
  return tabName
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
